import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const ODOO_PURPLE = "#875A7B";
const ODOO_GREEN = "#7EA800";


const benefits = [
  {
    id: 1,
    title: "Consulting And Assessment",
    description: "We can focus on your current operations, strategies, and challenges to provide expert guidance tailored to your specific needs. Through comprehensive assessments, consultants identify gaps, inefficiencies, and opportunities for growth. We offer the strategic insight needed to move your business forward with confidence.",
    icon: "🔗",
    color: ODOO_PURPLE,
    lightBg: "#F5EBF3"
  },
  {
    id: 2,
    title: "Workflow Design & Customization",
    description: "involve creating and tailoring business processes to match your organization’s unique needs and goals. We can start with mapping out the steps, roles, and tools involved in completing tasks efficiently.",
    icon: "🧩",
    color: ODOO_GREEN,
    lightBg: "#F0F8E9"
  },
  {
    id: 3,
    title: "Data Migration",
    description: "This process is essential when upgrading Odoo, switching from consolidating systems. It includes migrating key business data such as customer records, inventory, sales, accounting and more. Our successful Odoo data migration ensures data accuracy, consistency, and integrity.",
    icon: "⚡",
    color: "#F5A623",
    lightBg: "#FFF8E1"
  },
  {
    id: 4,
    title: "Integration",
    description: "This integration streamlines operations by automating data flow, reducing manual entry, and ensuring real-time synchronization across departments. With Odoo's modular design businesses can customize integrations to fit their specific needs and boosting efficiency, improving accuracy, and enhancing customer experience.",
    icon: "📊",
    color: "#007AFF",
    lightBg: "#E8F0FE"
  },
  {
    id: 5,
    title: "Training & Support",
    description: "We can provide expert assistance for troubleshooting, system updates, and user guidance. With the right training and responsive support, your business can reduce errors, improve productivity, and fully leverage Odoo’s integrated features and leading to smoother operations and greater long-term success.",
    icon: "☁️",
    color: "#50E3C2",
    lightBg: "#E8FFFB"
  },
  {
    id: 6,
    title: "Continuous Improvement",
    description: "We can optimize workflows, configurations, and integrations to better support evolving goals. By committing to continuous improvement, organizations stay competitive and aligned with best practices and market demands.",
    icon: "📈",
    color: "#FF2D55",
    lightBg: "#FFECEC"
  }
];

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const  OdooBenefitsSection=()=> {
  const [hovered, setHovered] = useState(null);

  return (
    <section className="relative py-16 bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 overflow-hidden">
      {/* Subtle Animated Blobs */}
      <motion.div
        className="absolute top-0 left-0 w-64 h-64 bg-[#F5EBF3] rounded-full mix-blend-multiply filter blur-3xl opacity-30"
        animate={{ x: [0, 100, 0], y: [0, 50, 0] }}
        transition={{ duration: 12, repeat: Infinity, repeatType: 'reverse' }}
      />
      <motion.div
        className="absolute bottom-10 right-0 w-72 h-72 bg-[#E8F0FE] rounded-full mix-blend-multiply filter blur-3xl opacity-25"
        animate={{ x: [0, -120, 0], y: [0, -60, 0] }}
        transition={{ duration: 14, repeat: Infinity, repeatType: 'reverse' }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
       
             <div className="text-center mb-16">
         
         <h1 className="text-4xl md:text-5xl font-bold text-[#2c2c54] mb-6 leading-tight">
          Our Odoo ERP 
           <span className="block text-3xl md:text-4xl bg-gradient-to-r from-[#714b67] to-[#8f4a7e] bg-clip-text text-transparent mt-2">
           Implementation Services
           </span>
         </h1>
         <p className="text-lg text-[#495057] max-w-2xl mx-auto text-center">
         KCS Tech offers complete end-to-end Odoo implementation services to ensure the configuration and deployment of Odoo in your business
         </p>
       </div>
        <div className="grid gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence>
            {benefits.map((b, i) => (
              <motion.div
                key={b.id}
                className="relative p-6 rounded-3xl shadow-lg"
                style={{ backgroundColor: b.lightBg }}
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                transition={{ delay: i * 0.1, duration: 0.6, ease: 'easeOut' }}
                onMouseEnter={() => setHovered(b.id)}
                onMouseLeave={() => setHovered(null)}
              >
                {/* Highlight overlay */}
                {hovered === b.id && (
                  <motion.div
                    className="absolute inset-0 rounded-3xl"
                    style={{ backgroundColor: b.color + '33' }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  />
                )}

                <div className="relative z-10 flex items-center justify-center w-16 h-16 rounded-full mb-4 shadow-md"
                  style={{ backgroundColor: b.color }}>
                  <span className="text-white text-2xl">{b.icon}</span>
                </div>

                <h3 className="text-xl font-semibold text-gray-800 mb-2 text-center">
                  {b.title}
                </h3>
                <p
                className="text-sm"
                style={{
                  color: '#4B5563', // Tailwind's text-gray-600
                  wordBreak: 'normal',        // Do not break words unless necessary
                  overflowWrap: 'anywhere',   // Break long words/URLs if needed, but not normal words
                  whiteSpace: 'normal',       // Allow normal wrapping
                  hyphens: 'auto',            // Use hyphens if the browser/language supports it
                  lineHeight: '1.7'
                }}
              >
                {b.description}
              </p>
              
  
                <div className="mt-4 flex justify-center">
                  {/* <motion.button
                    className="flex items-center gap-2 text-sm font-medium"
                    style={{ color: b.color }}
                    whileHover={{ x: 5 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    Learn More
                    <svg width="20" height="20" fill="none" stroke={b.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </motion.button> */}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* <div className="text-center mt-12">
          <button className="inline-flex items-center px-6 py-3 bg-[#875A7B] hover:bg-[#6f4f65] text-white font-semibold rounded-full shadow-lg transition-all duration-300">
            Explore All Services
          </button>
        </div> */}
      </div>
    </section>
  );
}

export default OdooBenefitsSection;